import type {
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { useId } from 'react';
import { Icone, type NomeDoIcone } from '@/shared/ui/icones';
import './campo.css';

export type TamanhoCampo = 'padrao' | 'alto' | 'miudo';

type Comum = {
  /** Rótulo visível. Sem ele, `rotuloOculto` passa a ser obrigatório. */
  rotulo?: string;
  /** Rótulo só para leitor de tela — busca e filtro, onde o rótulo é ruído. */
  rotuloOculto?: string;
  rotuloVersal?: boolean;
  /** Texto de apoio abaixo do rótulo. */
  ajuda?: string;
  /** Mensagem de erro. Liga `aria-invalid` e `aria-describedby`. */
  erro?: string;
  tamanho?: TamanhoCampo;
  /** Filtro com valor escolhido: fundo roxo-fraca. */
  ativo?: boolean;
};

/*
 * `ref` entra como prop comum, no idioma do React 19: quem abre um diálogo de
 * busca precisa levar o foco ao campo, e sem isto o diálogo de mensagens
 * prontas não compila.
 */
export type CampoProps = Comum &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'> & {
    ref?: Ref<HTMLInputElement>;
    /**
     * Um ícone dentro do campo, à esquerda: a lupa da busca. Só desenho; o que
     * o campo é continua dito pelo rótulo (ou `rotuloOculto`).
     */
    icone?: NomeDoIcone;
    /**
     * O campo acabou de receber um valor vindo de outro (#765: o plano
     * escolhido preenche o contrato). O fundo acende e volta, para a pessoa
     * ver o que mudou sem ter digitado. Para acender de novo, quem usa troca a
     * `key`; para escalonar vários, põe a posição em `--i` no `style`.
     */
    realce?: boolean;
  };

export type CampoAreaProps = Comum &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
    ref?: Ref<HTMLTextAreaElement>;
  };

export type CampoSelecaoProps = Comum &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
    ref?: Ref<HTMLSelectElement>;
    children?: ReactNode;
  };

function classes(tamanho: TamanhoCampo, extra?: string) {
  return [
    'campo__controle',
    tamanho === 'alto' ? 'campo__controle--alto' : null,
    tamanho === 'miudo' ? 'campo__controle--miudo' : null,
    extra ?? null,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Envelope compartilhado: rótulo, ajuda e erro amarrados por id. */
export function Envelope({
  id,
  rotulo,
  rotuloVersal,
  ajuda,
  erro,
  children,
}: {
  id: string;
  rotulo?: string;
  rotuloVersal?: boolean;
  ajuda?: string;
  erro?: string;
  children: ReactNode;
}) {
  return (
    <div className="campo">
      {rotulo ? (
        <label
          className={rotuloVersal ? 'campo__rotulo campo__rotulo--versal' : 'campo__rotulo'}
          htmlFor={id}
        >
          {rotulo}
        </label>
      ) : null}
      {ajuda ? (
        <p className="campo__ajuda" id={`${id}-ajuda`}>
          {ajuda}
        </p>
      ) : null}
      {children}
      {erro ? (
        <p className="campo__erro" id={`${id}-erro`} role="alert">
          {erro}
        </p>
      ) : null}
    </div>
  );
}

export function descritores(id: string, ajuda?: string, erro?: string) {
  const ids = [ajuda ? `${id}-ajuda` : null, erro ? `${id}-erro` : null].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
}

export function Campo({
  rotulo,
  rotuloOculto,
  rotuloVersal,
  ajuda,
  erro,
  tamanho = 'padrao',
  ativo,
  icone,
  realce = false,
  id: idRecebido,
  ...resto
}: CampoProps) {
  const gerado = useId();
  const id = idRecebido ?? gerado;
  const extra = [
    icone ? 'campo__controle--com-icone' : null,
    realce ? 'campo__controle--realce' : null,
  ]
    .filter(Boolean)
    .join(' ');
  const controle = (
    <input
      id={id}
      className={classes(tamanho, extra || undefined)}
      aria-label={rotulo ? undefined : rotuloOculto}
      aria-invalid={erro ? true : undefined}
      aria-describedby={descritores(id, ajuda, erro)}
      data-ativo={ativo ? 'sim' : undefined}
      {...resto}
    />
  );
  return (
    <Envelope id={id} rotulo={rotulo} rotuloVersal={rotuloVersal} ajuda={ajuda} erro={erro}>
      {/* O ícone por cima do campo, como o chevron da seleção: o clique atravessa. */}
      {icone ? (
        <span className="campo__com-icone">
          {controle}
          <span className="campo__icone" aria-hidden="true">
            <Icone nome={icone} />
          </span>
        </span>
      ) : (
        controle
      )}
    </Envelope>
  );
}

export function CampoArea({
  rotulo,
  rotuloOculto,
  rotuloVersal,
  ajuda,
  erro,
  tamanho = 'padrao',
  id: idRecebido,
  ...resto
}: CampoAreaProps) {
  const gerado = useId();
  const id = idRecebido ?? gerado;
  return (
    <Envelope id={id} rotulo={rotulo} rotuloVersal={rotuloVersal} ajuda={ajuda} erro={erro}>
      <textarea
        id={id}
        className={classes(tamanho, 'campo__controle--area')}
        aria-label={rotulo ? undefined : rotuloOculto}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descritores(id, ajuda, erro)}
        {...resto}
      />
    </Envelope>
  );
}

export function CampoSelecao({
  rotulo,
  rotuloOculto,
  rotuloVersal,
  ajuda,
  erro,
  tamanho = 'padrao',
  ativo,
  id: idRecebido,
  children,
  ...resto
}: CampoSelecaoProps) {
  const gerado = useId();
  const id = idRecebido ?? gerado;
  return (
    <Envelope id={id} rotulo={rotulo} rotuloVersal={rotuloVersal} ajuda={ajuda} erro={erro}>
      {/*
        O chevron é NOSSO, por cima do select, porque o `appearance: none` da
        base tira o desenho do navegador — e era esse desenho que saía de um
        jeito no Chrome e de outro no Safari. O span não recebe clique nem
        foco: o select continua sendo o controle, e a lista que abre continua
        nativa.
      */}
      <span className="campo__selecao">
        <select
          id={id}
          className={classes(tamanho, 'campo__controle--selecao')}
          aria-label={rotulo ? undefined : rotuloOculto}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descritores(id, ajuda, erro)}
          data-ativo={ativo ? 'sim' : undefined}
          {...resto}
        >
          {children}
        </select>
        <span className="campo__selecao-seta" aria-hidden="true">
          <Icone nome="abaixo" />
        </span>
      </span>
    </Envelope>
  );
}
